import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ProgramsEditor } from '../components/ProgramsEditor';
import { CampaignsEditor } from '../components/CampaignsEditor';
import { ConfigValuesEditor } from '../components/ConfigValuesEditor';
import '../styles/ListPages.css';
import '../styles/FormPages.css';

interface ConfigCard {
  id: string;
  title: string;
  description: string;
  icon: string;
  color: string;
}

const configOptions: ConfigCard[] = [
  {
    id: 'zipcodes',
    title: 'Service Area Zip Codes',
    description: 'Manage zip codes that qualify for WeCare Grant services and CANS assessments',
    icon: '📍',
    color: '#5B2C86'
  },
  {
    id: 'programs',
    title: 'Programs & Services',
    description: 'Manage available programs and services offered by the organization',
    icon: '🏢',
    color: '#FAA31B'
  },
  {
    id: 'contact-types',
    title: 'Contact Types',
    description: 'Manage contact methods available for logging service visits',
    icon: '📞',
    color: '#5B2C86'
  },
  {
    id: 'donor-interests',
    title: 'Donor Interest Areas',
    description: 'Manage areas of interest for donor engagement and fundraising',
    icon: '💝',
    color: '#FAA31B'
  },
  {
    id: 'campaigns',
    title: 'Fundraising Campaigns',
    description: 'Manage active and past fundraising campaigns',
    icon: '🎯',
    color: '#5B2C86'
  },
  {
    id: 'roles',
    title: 'User Roles',
    description: 'View and manage system user roles and permissions',
    icon: '👥',
    color: '#FAA31B'
  }
];

export function SystemConfigurationPage() {
  const [selectedConfig, setSelectedConfig] = useState<string | null>(null);

  return (
    <div className="list-page">
      <div className="page-header">
        <h2>System Configuration</h2>
        <p>Manage system-wide settings and configuration values</p>
      </div>

      <div style={{ marginBottom: '2rem', display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
        <Link to="/admin">
          <button className="button-secondary">
            👥 User Management
          </button>
        </Link>
        <Link to="/admin/announcements">
          <button className="button-secondary">
            📢 Announcements
          </button>
        </Link>
        <Link to="/admin/audit-logs">
          <button className="button-secondary">
            📋 Audit Logs
          </button>
        </Link>
      </div>

      {!selectedConfig ? (
        <>
          <div className="card" style={{ marginBottom: '2rem', backgroundColor: '#f8f9fa' }}>
            <h3>⚙️ Configuration Management</h3>
            <p style={{ marginTop: '1rem', lineHeight: '1.8' }}>
              Select a configuration area below to manage its values. Changes take effect immediately
              across the application. For user management, visit the <Link to="/admin">Admin Panel</Link>.
            </p>
          </div>

          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
            gap: '1.5rem',
            marginTop: '2rem'
          }}>
            {configOptions.map(option => (
              <div
                key={option.id}
                className="card"
                style={{
                  cursor: 'pointer',
                  border: `2px solid ${option.color}`,
                  transition: 'all 0.3s ease'
                }}
                onClick={() => setSelectedConfig(option.id)}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-4px)';
                  e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.15)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '';
                }}
              >
                <div style={{ display: 'flex', alignItems: 'start', gap: '1rem' }}>
                  <div style={{ fontSize: '2.5rem' }}>{option.icon}</div>
                  <div style={{ flex: 1 }}>
                    <h3 style={{ color: option.color, marginBottom: '0.5rem' }}>
                      {option.title}
                    </h3>
                    <p style={{ fontSize: '0.9rem', color: '#666', lineHeight: '1.6' }}>
                      {option.description}
                    </p>
                    <button
                      className="button-link"
                      style={{
                        marginTop: '1rem',
                        color: option.color,
                        fontWeight: 600
                      }}
                    >
                      Configure →
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="card" style={{ marginTop: '2rem', backgroundColor: '#fff3cd', border: '1px solid #ffc107' }}>
            <h4 style={{ marginBottom: '0.5rem' }}>⚠️ Important Notes</h4>
            <ul style={{ marginTop: '1rem', lineHeight: '1.8', paddingLeft: '1.5rem' }}>
              <li>Changes to configuration values take effect immediately</li>
              <li>Users may need to refresh their browser to see updates</li>
              <li>Deleting values that are currently in use may cause errors</li>
              <li>Only administrators can access this page</li>
            </ul>
          </div>
        </>
      ) : (
        <div>
          <button
            onClick={() => setSelectedConfig(null)}
            className="button-secondary"
            style={{ marginBottom: '1rem' }}
          >
            ← Back to Configuration Menu
          </button>

          <ConfigurationEditor configType={selectedConfig} />
        </div>
      )}
    </div>
  );
}

// Configuration editor component router
function ConfigurationEditor({ configType }: { configType: string }) {
  const config = configOptions.find(c => c.id === configType);

  if (!config) {return null;}

  // Render appropriate editor based on config type
  const renderEditor = () => {
    switch (configType) {
      case 'zipcodes':
        return (
          <ConfigValuesEditor
            categoryName="ServiceAreaZipCodes"
            title="Service Area Zip Code"
            keyLabel="Zip Code"
            keyPlaceholder="33127"
            valueLabel="Display Label"
            valuePlaceholder="33127 - Liberty City"
          />
        );

      case 'programs':
        return <ProgramsEditor />;

      case 'contact-types':
        return (
          <ConfigValuesEditor
            categoryName="ContactTypes"
            title="Contact Type"
            keyLabel="Type Code"
            keyPlaceholder="Phone"
            valueLabel="Display Label"
            valuePlaceholder="Phone Contact"
          />
        );

      case 'donor-interests':
        return (
          <ConfigValuesEditor
            categoryName="DonorInterests"
            title="Donor Interest Area"
            keyLabel="Interest Code"
            keyPlaceholder="Youth Programs"
            valueLabel="Display Label"
            valuePlaceholder="Youth Programs"
          />
        );

      case 'campaigns':
        return <CampaignsEditor />;

      case 'roles':
        return (
          <div className="card" style={{ padding: '2rem', backgroundColor: '#f8f9fa', textAlign: 'center' }}>
            <p style={{ fontSize: '1.1rem', color: '#666' }}>
              User roles are managed through the <Link to="/admin">User Management</Link> page.
            </p>
            <p style={{ marginTop: '1rem', fontSize: '0.9rem', color: '#999' }}>
              Roles: Admin, Supervisor, Success Coach, Viewer
            </p>
          </div>
        );

      default:
        return (
          <div className="card" style={{ padding: '2rem', backgroundColor: '#f8f9fa', textAlign: 'center' }}>
            <p style={{ fontSize: '1.1rem', color: '#666' }}>
              Configuration editor for <strong>{config.title}</strong> coming soon...
            </p>
          </div>
        );
    }
  };

  return (
    <div>
      <div className="card" style={{ marginBottom: '1.5rem' }}>
        <h2 style={{ color: config.color }}>
          {config.icon} {config.title}
        </h2>
        <p style={{ marginTop: '0.5rem', color: '#666' }}>{config.description}</p>
      </div>

      {renderEditor()}
    </div>
  );
}
